import { useState, Fragment, useEffect } from 'react';
import { PDFImageProps, VTAnomalyData, VTCoverData } from '../../../../utils/models';

import Button from '../../../themed/button';
import Conditional from '../../../common/conditional';
import Elevation from '../../../themed/instruction-modals/elevation';
import Modal from '../../../themed/modal';
import RiskRating from '../riskRating';
import VTPipingReport from '.';
import VTCoverForm from '../../../forms/vt/vtCoverForm';
import VTPipingSummary from './vtPipingSummary';
import VTPipingReportSummary from '../../../forms/vt/vtPipingReportSummary';
import VTPhotoRecordForm from '../../../forms/vt/vtPhotoRecordForm';
import Zippy from '../../../themed/zippy';

import { tutorialConstants } from '../../../../utils/constants';
import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

import config from '../../../../utils/config';

const CreateVTPipingReport = () => {
	const [vtCoverData, setVTCoverData] = useState<VTCoverData | null>();
	const [vtPipingData, setVTPipingData] = useState<VTAnomalyData[]>([]);
	const [vtPipingSummary, setVTPipingSummary] = useState('');
	const [vtPhoto, setVTPhoto] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			anomalyData: vtPipingData,
			vtPipingReportSummary: vtPipingSummary,
			vtSummaryData: vtPipingData,
			coverSheetData: vtCoverData,
			photoRecord: vtPhoto,
			projectDetails,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setVTCoverData(cachedReport.coverSheetData);
			setVTPipingData(cachedReport.vtSummaryData);
			setVTPipingSummary(cachedReport.vtPipingReportSummary);
			setVTPhoto(cachedReport.photoRecord);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<VTPipingReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!vtCoverData}>
				<VTCoverForm onProceed={setVTCoverData} />
			</Zippy>

			<Zippy headerTitle="Report Summary" isValid={vtPipingSummary.length > 0}>
				<VTPipingReportSummary onProceed={setVTPipingSummary} />
			</Zippy>

			<Zippy headerTitle="Anomaly Sheet" isValid={vtPipingData.length > 0}>
				<VTPipingSummary
					anomalyData={vtPipingData}
					onDataChange={setVTPipingData}
				/>
			</Zippy>

			<Zippy headerTitle="Photographic Record" isValid={vtPhoto.length > 0}>
				<VTPhotoRecordForm onProceed={setVTPhoto} />
			</Zippy>

			<Conditional isVisible={config.enableRiskRating}>
				<Zippy headerTitle="Risk Rating" isValid={vtPipingData.length > 0}>
					<RiskRating data={vtPipingData} />
				</Zippy>
			</Conditional>

			<div className="d-flex justify-content-end">
				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateVTPipingReport;
